/**
 * `useLateFeeStrip` — Sketch 022 inline late-fee strip data source.
 *
 * Drives the amber strip rendered between `ServiceSummary`'s hero block and
 * the meta strip. Replaces the standalone `ArrivalDetails.vue` card; the
 * compute that used to live there now lives here so the strip can be
 * embedded inside the service-details card.
 *
 * Visibility gate (CONTEXT D-11):
 *   - dispatch_time is set                           (tech has been dispatched;
 *                                                     v2 wire — adapter
 *                                                     normalises v1
 *                                                     `iso_dispatch_time`)
 *   - creditCents > 0                                (there is an ACTUAL credit to
 *                                                     show). This single check
 *                                                     subsumes the prior
 *                                                     lifecycle / fees-on /
 *                                                     past-window conditions:
 *                                                     `creditCents` is only > 0
 *                                                     when the lifecycle is
 *                                                     'active', per-minute + cap
 *                                                     are both > 0, and `lateMins`
 *                                                     (frozen at `arrival_time`)
 *                                                     is > 0. So the strip no
 *                                                     longer renders a $0 / 0% bar
 *                                                     when the tech arrived on
 *                                                     time or fees are
 *                                                     misconfigured.
 *
 * Math:
 *   - `effectiveNow` — the freeze point: `appointment.arrival_time` (ms) once
 *                       the tech has arrived, otherwise the live `now`. Once
 *                       `arrival_time` is set the credit STOPS growing, frozen
 *                       at the value computed from arrival (legacy parity with
 *                       consumer-portal `LateFeeTimer.vue:114-121`). Before
 *                       arrival the credit still grows off the 1s tick.
 *   - `lateMins`     — floor((effectiveNow - arrival_window_end) / 60_000);
 *                       short-circuits to 0 when `mapStatusToLifecycle(status)`
 *                       is not 'active' (completed/cancelled never accrue a fee,
 *                       so `creditCents`/`fillPercent`, which derive from
 *                       `lateMins`, also yield 0).
 *   - `creditCents`  — min(maxLateFee, lateMins * perMinCents)
 *   - `capCents`     — settings.max_late_fee_cents
 *   - `fillPercent`  — min(100, lateMins / capMins * 100) where
 *                       capMins = capCents / perMinCents
 *
 * The 1-second tick uses `useIntervalFn(1000)` from `@vueuse/core` so the
 * progress bar animates without any imperative interval bookkeeping in
 * the consumer. The interval is automatically stopped on component
 * unmount.
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import type { Appointment } from '../stores/useAppointmentsStore';
import { mapStatusToLifecycle } from '../utils/appointment-status';

export interface LateFeeStrip {
  visible: ComputedRef<boolean>;
  lateMins: ComputedRef<number>;
  creditCents: ComputedRef<number>;
  capCents: ComputedRef<number>;
  fillPercent: ComputedRef<number>;
}

/** Parse an ISO date string to ms, or `NaN` when absent/unparseable. */
function toFiniteMs(iso: string | null | undefined): number {
  if (!iso) return Number.NaN;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : Number.NaN;
}

export function useLateFeeStrip(
  appointment: Ref<Appointment | null>,
): LateFeeStrip {
  // Single 1s cadence source: the interval rewrites `now` to match the legacy
  // timer cadence and keeps the progress bar smooth without burning frame
  // budget. `useIntervalFn` is automatically stopped on component unmount.
  const now = ref<Date>(new Date());
  useIntervalFn(() => {
    now.value = new Date();
  }, 1000);

  const perMinCents = computed<number>(
    () => appointment.value?.settings?.late_fee_per_minute_cents ?? 0,
  );

  const capCents = computed<number>(
    () => appointment.value?.settings?.max_late_fee_cents ?? 0,
  );

  const windowEndMs = computed<number>(() =>
    toFiniteMs(appointment.value?.arrival_window_end),
  );

  // Freeze point (see module "Math" doc): arrival_time once set, else live now.
  const effectiveNowMs = computed<number>(() => {
    const arrivalMs = toFiniteMs(appointment.value?.arrival_time);
    return Number.isFinite(arrivalMs) ? arrivalMs : now.value.getTime();
  });

  const lateMins = computed<number>(() => {
    // completed/cancelled never accrue a fee — 0 flows to credit/fillPercent.
    const lifecycle = mapStatusToLifecycle(appointment.value?.status ?? '');
    const end = windowEndMs.value;
    if (lifecycle !== 'active' || !Number.isFinite(end)) return 0;
    const diff = effectiveNowMs.value - end;
    return diff <= 0 ? 0 : Math.floor(diff / 60_000);
  });

  const creditCents = computed<number>(() => {
    // A non-positive cap means "no fee surface", not "no ceiling": the v1 wire
    // sets per-minute and cap to 0 together when fees are off. Treating cap<=0
    // as uncapped would let the credit run away while fillPercent stays at 0%.
    if (perMinCents.value <= 0 || capCents.value <= 0) return 0;
    return Math.min(capCents.value, lateMins.value * perMinCents.value);
  });

  const fillPercent = computed<number>(() => {
    if (perMinCents.value <= 0 || capCents.value <= 0) return 0;
    const capMins = capCents.value / perMinCents.value;
    if (capMins <= 0) return 0;
    return Math.min(100, (lateMins.value / capMins) * 100);
  });

  const visible = computed<boolean>(() => {
    const appt = appointment.value;
    if (!appt) return false;
    if (!appt.dispatch_time) return false;
    // Show the strip only when there is an actual credit. `creditCents > 0`
    // already encodes active lifecycle, fees-on, a positive cap, and a non-zero
    // `lateMins` frozen at `arrival_time` — so an on-time arrival (or a past
    // window with no accrued minutes) keeps the strip hidden instead of showing
    // a $0 / 0% bar.
    return creditCents.value > 0;
  });

  return { visible, lateMins, creditCents, capCents, fillPercent };
}
